import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { qualificationScore, type Qualification } from '@/types/crm'
import { QUALIFICATION_CRITERIA, scoreStyle } from '../qualification'

interface Props {
  companyName: string
  qualification: Qualification
  onClose: () => void
  onSave: (qualification: Qualification) => Promise<void>
}

export function QualificationModal({ companyName, qualification, onClose, onSave }: Props) {
  const [form, setForm] = useState<Qualification>(qualification)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const score = qualificationScore(form)
  const style = scoreStyle(score)

  const submit = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await onSave({ ...form, comment: form.comment.trim() })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
      setSaving(false)
    }
  }

  return (
    <Modal
      title="Lead qualification"
      subtitle={`Score ${companyName} on three criteria. The score drives the table sort, so keep it honest.`}
      onClose={onClose}
      width={480}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="modal__error">{error}</div>}

      <div className="qualmodal__preview">
        <span className="pill" style={{ background: style.bg, color: style.color }}>
          {style.label} · {score}/3
        </span>
      </div>

      {QUALIFICATION_CRITERIA.map((c) => {
        const on = form[c.key]
        return (
          <button
            key={c.key}
            type="button"
            className="qualmodal__row"
            aria-pressed={on}
            onClick={() => setForm((f) => ({ ...f, [c.key]: !f[c.key] }))}
          >
            <span className="qualmodal__rowtext">
              <span className="qualmodal__label">{c.label}</span>
              <span className="qualmodal__hint">{c.hint}</span>
            </span>
            <span className={`toggle${on ? ' toggle--on' : ''}`} aria-hidden="true">
              <span className="toggle__knob" />
            </span>
          </button>
        )
      })}

      <label className="field">
        <span>Comment</span>
        <textarea
          className="input"
          rows={3}
          value={form.comment}
          onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
          placeholder="Why this score? Any context for the next rep…"
        />
      </label>
    </Modal>
  )
}
